package com.example.monetix.utils

import com.example.monetix.TYPE_EXPENSE
import com.example.monetix.TYPE_INCOME
import com.example.monetix.TYPE_TRANSFER
import com.example.monetix.db.CategoryEntity
import com.example.monetix.db.FinancingEntity
import com.example.monetix.db.RecurringExpenseEntity
import com.example.monetix.db.SavingsGoalEntity
import com.example.monetix.db.TransactionEntity
import com.example.monetix.stripSystemMarker
import kotlinx.datetime.*
import kotlin.math.abs

// 🚀 FUNZIONE DI PULIZIA GLOBALE
fun String.cleanDisplayName(): String {
    return stripSystemMarker(this)?.trim() ?: ""
}

// 🚀 REGOLA UNIVERSALE PER RICONOSCERE I DI CUI IN CONTANTI (A prova di bomba)
fun isCashSubTx(tx: TransactionEntity): Boolean {
    val key = (tx.merchantKey ?: "").lowercase()
    val desc = (tx.description ?: "").lowercase()
    return tx.id.startsWith("tx_cash_") ||
            tx.accountId?.startsWith("sys_cash_") == true ||
            key.contains("[cash:") ||
            desc.contains("[cash:")
}
data class MonthlyStats(
    val incomeCents: Long,
    val expenseCents: Long,
    val unpaidRecurringCents: Long,
    val unpaidFinancingCents: Long,
    val unpaidRecurringList: List<RecurringExpenseEntity>,
    val unpaidFinancingList: List<FinancingEntity>,
    val expectedRecurringList: List<RecurringExpenseEntity>,
    val expectedFinancingList: List<FinancingEntity>,
    val forecastEndBalance: Long,
    val totalFixedCents: Long,
    val savingsTransferCents: Long = 0L,
    val prevSavingsTransferCents: Long = 0L,
    val prevYearSavingsTransferCents: Long = 0L,
    val savingsHistory: List<Pair<String, Long>> = emptyList(),
    val balanceHistory: List<Pair<String, Long>> = emptyList(),
    val prevExpenseCents: Long = 0L,
    val pieData: List<Pair<String, Long>> = emptyList(),
    val topMerchants: List<Pair<String, Long>> = emptyList(),
    val remainingRecurringCents: Long = 0L,
    val remainingFinancingCents: Long = 0L,
    val totalPeriodRecurringCents: Long = 0L,
    val totalPeriodFinancingCents: Long = 0L
)

object StatsEngine {

    private fun lengthOfMonth(year: Int, month: Int): Int {
        val isLeap = (year % 4 == 0 && (year % 100 != 0 || year % 400 == 0))
        return when (month) {
            2 -> if (isLeap) 29 else 28
            4, 6, 9, 11 -> 30
            else -> 31
        }
    }

    private fun LocalDate.minusMonthsCustom(months: Int): LocalDate {
        var newMonth = this.monthNumber - months
        var newYear = this.year
        while (newMonth <= 0) {
            newMonth += 12
            newYear -= 1
        }
        val maxDays = lengthOfMonth(newYear, newMonth)
        return LocalDate(newYear, newMonth, this.dayOfMonth.coerceAtMost(maxDays))
    }

    fun calculateMonthlyStats(
        startDate: LocalDate,
        endDate: LocalDate,
        latestTx: List<TransactionEntity>,
        categories: List<CategoryEntity>,
        recurringItems: List<RecurringExpenseEntity>,
        financingItems: List<FinancingEntity>,
        activeAccountId: String?,
        availableBalanceCents: Long,
        savingsAccountIds: Set<String> = emptySet(),
        historyOffsetMonths: Int = 0
    ): MonthlyStats {

        val startEpoch = startDate.toEpochDays().toLong()
        val endEpoch = endDate.toEpochDays().toLong()
        val today = Clock.System.todayIn(TimeZone.currentSystemDefault())
        val todayEpoch = today.toEpochDays().toLong()

        val monthTx = latestTx.filter {
            !it.isDeleted && it.dateEpochDay in startEpoch..endEpoch
        }.filter {
            activeAccountId.isNullOrBlank() || (it.accountId == activeAccountId || it.fromAccountId == activeAccountId || it.toAccountId == activeAccountId)
        }.filter {
            !isCashSubTx(it) // 🚀 FIX: Escludi i "di cui in contanti" dalle statistiche mensili, dai saldi e dai grafici a torta!
        }

        val prevStartEpoch = startDate.minusMonthsCustom(1).toEpochDays().toLong()
        val prevEndEpoch = startDate.minus(DatePeriod(days = 1)).toEpochDays().toLong()

        val prevMonthTx = latestTx.filter {
            !it.isDeleted && it.dateEpochDay in prevStartEpoch..prevEndEpoch
        }.filter {
            activeAccountId.isNullOrBlank() || (it.accountId == activeAccountId || it.fromAccountId == activeAccountId || it.toAccountId == activeAccountId)
        }.filter {
            !isCashSubTx(it) // 🚀 FIX: Anche il mese precedente deve essere pulito per non sballare le differenze percentuali!
        }

        fun hasOccurrenceInPeriod(targetDay: Int, itemStartEpoch: Long, itemEndEpoch: Long?, pStart: LocalDate, pEnd: LocalDate): Boolean {
            val itemStartDate = LocalDate.fromEpochDays(itemStartEpoch.toInt())
            if (pEnd < itemStartDate) return false
            if (itemEndEpoch != null && pStart > LocalDate.fromEpochDays(itemEndEpoch.toInt())) return false

            var current = if (pStart < itemStartDate) itemStartDate else pStart
            while (current <= pEnd) {
                if (itemEndEpoch != null && current > LocalDate.fromEpochDays(itemEndEpoch.toInt())) return false
                val effectiveTargetDay = targetDay.coerceAtMost(lengthOfMonth(current.year, current.monthNumber))
                if (current.dayOfMonth == effectiveTargetDay) return true
                current = current.plus(DatePeriod(days = 1))
            }
            return false
        }

        fun isItemPaid(itemId: String, itemName: String?, itemCatId: String?, itemAmountCents: Long, isTransfer: Boolean, fromAccId: String? = null, toAccId: String? = null): Boolean {
            val cleanItem = itemName?.uppercase()?.trim()?.replace(" ", "") ?: ""
            // 🚀 FIX CRUCIALE: Non cerchiamo solo in "monthTx" (che è limitato alla fine del mese contabile), 
            // ma in TUTTO latestTx a partire dall'inizio del mese!
            return latestTx.any { tx ->
                if (tx.isDeleted || tx.dateEpochDay < startEpoch) return@any false

                val matchType = if (isTransfer) tx.type == TYPE_TRANSFER else tx.type == TYPE_EXPENSE
                if (!matchType) return@any false

                if (itemId.isNotEmpty()) {
                    if (tx.id.contains(itemId)) return@any true
                    if (tx.description?.contains(itemId) == true) return@any true
                }

                val mKey = tx.merchantKey ?: ""
                if (itemId.isNotEmpty() && mKey.contains(itemId)) return@any true

                if (tx.amountCents == itemAmountCents) {
                    if (isTransfer) {
                        if (tx.fromAccountId == fromAccId && tx.toAccountId == toAccId) return@any true
                    } else {
                        val cleanTxDesc = (tx.description ?: "").uppercase().replace(" ", "")
                        val cleanTxMerchant = mKey.uppercase().replace(" ", "")
                        if ((cleanItem.isNotEmpty() && (cleanTxDesc.contains(cleanItem) || cleanTxMerchant.contains(cleanItem))) || (itemCatId != null && tx.categoryId == itemCatId)) return@any true
                    }
                }
                false
            }
        }

        // 🚀 CERVELLO PRO: IGNORIAMO LE ENTRATE DAI DEBITI FISSI DA PAGARE!
        val expectedRecList = recurringItems
            .filter { !it.isDeleted && it.isEnabled }
            // 🚀 FIX CRUCIALE: Escludiamo i flussi in ingresso dal calcolo dei debiti
            .filter { it.type != "INCOME" }
            .filter { activeAccountId.isNullOrBlank() || it.accountId == activeAccountId }
            .filter {
                val itemStartEpochDay = it.startEpochDay
                hasOccurrenceInPeriod(it.dayOfMonth.toInt(), itemStartEpochDay, null, startDate, endDate)
            }
            .filter {
                // 🚀 FIX: Nella visione GLOBALE (Tutti i conti), un giroconto tra conti NON deve MAI
                // abbattere le statistiche di patrimonio.
                !(activeAccountId.isNullOrBlank() && it.type == "TRANSFER" && it.toAccountId != null && it.toAccountId != "null")
            }

        val expectedFinList = financingItems.filter { !it.isDeleted && !it.isClosed }.filter { activeAccountId.isNullOrBlank() || it.accountId == activeAccountId }.filter { fin ->
            val dueDay = try { LocalDate.fromEpochDays(fin.startEpochDay.toInt()).dayOfMonth } catch (_: Exception) { 1 }
            hasOccurrenceInPeriod(dueDay, fin.startEpochDay, fin.endEpochDay, startDate, endDate)
        }

        val totalRecCents = expectedRecList.sumOf { it.amountCents }
        val totalFinCents = expectedFinList.sumOf { it.installmentAmountCents + (it.commissionCents ?: 0L) }

        val unpaidRecList = expectedRecList.filter { !isItemPaid(it.id ?: "", it.name, it.categoryId, it.amountCents, (it.toAccountId ?: "") != "") }
        val unpaidFinList = expectedFinList.filter { !isItemPaid(it.id ?: "", it.name, it.categoryId, it.installmentAmountCents, false) }

        val remRec = unpaidRecList.sumOf { it.amountCents }
        val remFin = unpaidFinList.sumOf { it.installmentAmountCents + (it.commissionCents ?: 0L) }

        val incomeCents = monthTx.sumOf { if (it.type == TYPE_INCOME || (it.type == TYPE_TRANSFER && it.toAccountId == activeAccountId)) it.amountCents else 0L }
        val expenseCents = monthTx.sumOf { if (it.type == TYPE_EXPENSE || (it.type == TYPE_TRANSFER && it.fromAccountId == activeAccountId)) it.amountCents else 0L }
        val prevExpenseCents = prevMonthTx.sumOf { if (it.type == TYPE_EXPENSE || (it.type == TYPE_TRANSFER && it.fromAccountId == activeAccountId)) it.amountCents else 0L }

        val categoryNameById = categories.associate { (it.id ?: "") to it.name }

        val pieData = monthTx.filter { it.type == TYPE_EXPENSE && (it.toAccountId ?: "") == "" }.groupBy { categoryNameById[it.categoryId ?: ""] ?: "ALTRO" }
            .map { entry -> entry.key to entry.value.sumOf { abs(it.amountCents) } }
            .filter { it.second > 0 && !it.first.contains("ENTRAT", true) && !it.first.contains("STIPENDI", true) }
            .sortedByDescending { it.second }.take(9)

        val topMerchants = monthTx.filter { it.type == TYPE_EXPENSE && (it.toAccountId ?: "") == "" }.groupBy { tx ->
            val rawName = if (!tx.merchantKey.isNullOrBlank()) tx.merchantKey else tx.description
            val stripped = stripSystemMarker(rawName ?: "") ?: ""
            (stripped.ifBlank { rawName ?: "" }).trim().uppercase()
        }.map { it.key to it.value.sumOf { tx -> abs(tx.amountCents) } }.filter { it.first.isNotBlank() && it.second > 0 }.sortedByDescending { it.second }.take(5)

        val historyStarts = startDate.minusMonthsCustom(5).toEpochDays().toLong()

        val savingsHistory = latestTx.filter { !it.isDeleted && it.dateEpochDay in historyStarts..endEpoch }
            .filter { it.type == TYPE_TRANSFER && (activeAccountId == null || it.fromAccountId == activeAccountId) && (savingsAccountIds.isEmpty() || savingsAccountIds.contains(it.toAccountId ?: "")) }
            .groupBy { val d = LocalDate.fromEpochDays(it.dateEpochDay.toInt()); "${d.year}-${d.monthNumber.toString().padStart(2, '0')}" }
            .map { it.key to it.value.sumOf { tx -> tx.amountCents } }

        val balanceHistoryList = mutableListOf<Pair<String, Long>>()
        for (i in (0 + historyOffsetMonths)..(5 + historyOffsetMonths)) {
            val pastCycleEnd = endDate.minusMonthsCustom(i)
            val pastCycleEndEpoch = pastCycleEnd.toEpochDays().toLong()

            val txAfterPastCycle = latestTx.filter { !it.isDeleted && it.dateEpochDay > pastCycleEndEpoch && it.dateEpochDay <= todayEpoch }.filter { activeAccountId.isNullOrBlank() || (it.accountId == activeAccountId || it.fromAccountId == activeAccountId || it.toAccountId == activeAccountId) }
            var deltaCents = 0L
            txAfterPastCycle.forEach { tx ->
                when (tx.type) {
                    TYPE_INCOME -> deltaCents += tx.amountCents
                    TYPE_EXPENSE -> deltaCents -= tx.amountCents
                    TYPE_TRANSFER -> if (activeAccountId != null) { if (tx.toAccountId == activeAccountId) deltaCents += tx.amountCents; if (tx.fromAccountId == activeAccountId) deltaCents -= tx.amountCents }
                }
            }
            balanceHistoryList.add("${pastCycleEnd.monthNumber}/${pastCycleEnd.year}" to (availableBalanceCents - deltaCents))
        }

        // 🚀 MAGIA PURA: CALCOLO PREVISIONE MATEMATICA E NON TESTUALE
        val finalForecast: Long = if (today > startDate && today <= endDate) {
            val daysPassed = (today.toEpochDays() - startDate.toEpochDays()).toLong().coerceAtLeast(1L)
            val daysRemaining = (endDate.toEpochDays() - today.toEpochDays()).toLong().coerceAtLeast(0L)

            val totalFixedExpected = totalRecCents + totalFinCents
            val unpaidFixed = remRec + remFin

            // Le fisse già pagate
            val paidFixedCents = maxOf(0L, totalFixedExpected - unpaidFixed)

            // La spesa variabile pura = (Tutto quello che è uscito) - (La quota di uscite fisse)
            val totalSpentSoFar = abs(expenseCents)
            val variableSpentSoFar = maxOf(0L, totalSpentSoFar - paidFixedCents)

            val dailyVariableCents = variableSpentSoFar / daysPassed
            val estimatedRemainingVariableCents = dailyVariableCents * daysRemaining

            availableBalanceCents - estimatedRemainingVariableCents
        } else { availableBalanceCents }

        return MonthlyStats(
            unpaidRecurringCents = remRec,
            unpaidFinancingCents = remFin,
            unpaidRecurringList = unpaidRecList,
            unpaidFinancingList = unpaidFinList,
            expectedRecurringList = expectedRecList,
            expectedFinancingList = expectedFinList,
            incomeCents = incomeCents,
            expenseCents = expenseCents,
            forecastEndBalance = finalForecast,
            totalFixedCents = totalRecCents + totalFinCents,
            pieData = pieData,
            topMerchants = topMerchants,
            savingsHistory = savingsHistory,
            balanceHistory = balanceHistoryList.reversed(),
            totalPeriodRecurringCents = totalRecCents,
            totalPeriodFinancingCents = totalFinCents,
            remainingRecurringCents = remRec,
            remainingFinancingCents = remFin
        )
    }
}
fun calculateTotalStashed(
    savingsGoals: List<SavingsGoalEntity>,
    unpaidRecurringCents: Long,
    unpaidFinancingCents: Long
): Long {
    val totalInSogni = savingsGoals.filter { !it.isDeleted }.sumOf { it.savedAmountCents }
    val totalForScadenze = unpaidRecurringCents + unpaidFinancingCents
    return totalInSogni + totalForScadenze
}